import type { Invoice, ListParams } from "../../services/financialService";
import type { ExportColumn } from "../../utils/exportTable";

export const INVOICE_STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  sent: "Unpaid",
  paid: "Paid",
  overdue: "Overdue",
  credited: "Credited",
  cancelled: "Cancelled",
};

export const INVOICE_STATUS_DOTS: Record<string, string> = {
  draft: "bg-gray-400",
  sent: "bg-amber-500",
  paid: "bg-emerald-500",
  overdue: "bg-red-500",
  credited: "bg-sky-500",
  cancelled: "bg-gray-400",
};

/** Server ordering fields (InvoiceListView.ordering_fields). */
export const INVOICE_ORDERINGS = {
  "-created_at": "Newest issued first",
  created_at: "Oldest issued first",
  due_date: "Due soonest",
  "-due_date": "Due latest",
  "-total_amount": "Amount (highest first)",
  total_amount: "Amount (lowest first)",
  invoice_number: "Invoice number",
} as const;

export const INVOICE_PAGE_SIZES = [10, 25, 50, 100];

export interface InvoiceRegisterFilters {
  search: string;
  status: string;
  /** Only invoices with money still owed on them (sent or overdue). */
  outstanding: boolean;
  classId: string;
  dueFrom: string;
  dueTo: string;
  issuedFrom: string;
  issuedTo: string;
  ordering: keyof typeof INVOICE_ORDERINGS;
}

export const EMPTY_INVOICE_FILTERS: InvoiceRegisterFilters = {
  search: "",
  status: "",
  outstanding: false,
  classId: "",
  dueFrom: "",
  dueTo: "",
  issuedFrom: "",
  issuedTo: "",
  ordering: "-created_at",
};

/** The quick views above the filters. Each is a status choice, so they never disagree with the Status list. */
export const INVOICE_VIEWS = {
  all: { label: "All", filters: { status: "", outstanding: false } },
  outstanding: { label: "Outstanding", filters: { status: "", outstanding: true } },
  overdue: { label: "Overdue", filters: { status: "overdue", outstanding: false } },
  paid: { label: "Paid", filters: { status: "paid", outstanding: false } },
} as const;
export type InvoiceView = keyof typeof INVOICE_VIEWS;

export function activeInvoiceView(filters: InvoiceRegisterFilters): InvoiceView | null {
  return (
    (Object.keys(INVOICE_VIEWS) as InvoiceView[]).find((view) => {
      const preset = INVOICE_VIEWS[view].filters;
      return preset.status === filters.status && preset.outstanding === filters.outstanding;
    }) ?? null
  );
}

export const invoiceStatusLabel = (status: string) => INVOICE_STATUS_LABELS[status] ?? status.replace(/_/g, " ");

export function invoiceRegisterParams(filters: InvoiceRegisterFilters, search = filters.search): ListParams {
  return {
    search: search.trim(),
    status: filters.status,
    outstanding: filters.outstanding ? "true" : undefined,
    student__current_class: filters.classId,
    due_from: filters.dueFrom,
    due_to: filters.dueTo,
    issued_from: filters.issuedFrom,
    issued_to: filters.issuedTo,
    ordering: filters.ordering,
  };
}

/** Narrowing filters; a date range counts once and the sort order not at all. */
export const countInvoiceFilters = (filters: InvoiceRegisterFilters) =>
  [
    filters.search.trim(),
    filters.status || filters.outstanding,
    filters.classId,
    filters.dueFrom || filters.dueTo,
    filters.issuedFrom || filters.issuedTo,
  ].filter(Boolean).length;

const dayNumber = (iso: string) => {
  const [year, month, day] = iso.slice(0, 10).split("-").map(Number);
  return Date.UTC(year, month - 1, day) / 86_400_000;
};

/** Days past its due date an unpaid invoice is, or 0 when it isn't late. */
export function daysOverdue(invoice: Pick<Invoice, "due_date" | "status" | "balance_due">, today = new Date()) {
  if (!["sent", "overdue"].includes(invoice.status) || Number(invoice.balance_due) <= 0) return 0;
  const todayNumber = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()) / 86_400_000;
  return Math.max(0, todayNumber - dayNumber(invoice.due_date));
}

export const invoiceIssuedOn = (invoice: Invoice) => invoice.created_at?.slice(0, 10) ?? "";

export const invoiceBilling = (invoice: Invoice) => invoice.fee_structure_name || "Direct invoice";

export function invoiceExportColumns(): ExportColumn<Invoice>[] {
  return [
    { header: "Invoice no.", accessor: (invoice) => invoice.invoice_number },
    { header: "Issued", accessor: invoiceIssuedOn },
    { header: "Due", accessor: (invoice) => invoice.due_date },
    { header: "Student", accessor: (invoice) => invoice.student_name },
    { header: "Class", accessor: (invoice) => invoice.class_name || "" },
    { header: "Billed from", accessor: invoiceBilling },
    { header: "Total (UGX)", accessor: (invoice) => Number(invoice.total_amount) },
    { header: "Paid (UGX)", accessor: (invoice) => Number(invoice.paid_amount) },
    { header: "Credited (UGX)", accessor: (invoice) => Number(invoice.credited_amount || 0) },
    { header: "Balance (UGX)", accessor: (invoice) => Number(invoice.balance_due) },
    { header: "Status", accessor: (invoice) => invoiceStatusLabel(invoice.status) },
    { header: "Notes", accessor: (invoice) => invoice.notes || "" },
  ];
}
